// @flow

import React, { type Node, useEffect, useMemo } from 'react';
import { usePaymentSpec } from '@tectonics/prs/payment-spec';
import { useUserInputValue } from '@tectonics/prs/user-input-values';
import { useIncomingPayment, getPaymentType, isRetry } from '@tectonics/prs/incoming-payment';
import { type LookAndFeel } from '@tectonics/look-and-feel';
import { Empty } from './empty';

export function SetInitialPaymentMethod({ lookAndFeel }: { lookAndFeel: LookAndFeel }): Node {
  const [, setSelectedPaymentMethod] = useUserInputValue('selectedPaymentMethod');
  const incomingPayment = useIncomingPayment();
  const paymentType = getPaymentType(incomingPayment);
  const retry = isRetry(incomingPayment);
  const paymentSpec = usePaymentSpec();

  const specPaymentMethods = useMemo(
    () => paymentSpec.paymentMethods.map((element) => element.code),
    [paymentSpec]);

  const supportedPaymentMethods = useMemo(
    () => lookAndFeel.prsSupport.integrations[paymentSpec.country].filter(
      (code) => specPaymentMethods.includes(code)),
    [lookAndFeel, paymentSpec, specPaymentMethods]
  );

  if (supportedPaymentMethods.length < 1) {
    throw new Error('Look and feel does not support any Payment Method provided by Spec');
  }

  useEffect(() => {
    if (retry) {
      const indexOfPaymentType = supportedPaymentMethods.indexOf(paymentType);
      const selectedPaymentMethod = supportedPaymentMethods[~indexOfPaymentType ? indexOfPaymentType : 0];
      setSelectedPaymentMethod(selectedPaymentMethod);
    } else {
      setSelectedPaymentMethod(supportedPaymentMethods[0]);
    }
  }, [supportedPaymentMethods.join(), paymentType, retry, setSelectedPaymentMethod]);

  return <Empty />;
}
